/* Paging math for the notification list. Pure — no React, no state. */

/** Notifications shown per page in the panel. */
export const NOTIFICATIONS_PER_PAGE = 5;

export interface NotificationPage<T> {
  /** The items belonging to the requested page. */
  pageItems: T[];
  /** Total number of pages. At least 1, so the control never renders zero pages. */
  numberOfPages: number;
  /** The page actually used, clamped into range. */
  currentPage: number;
  /** 1-based index of the first item on the page, or 0 when there are no items. */
  rangeStart: number;
  /** 1-based index of the last item on the page, or 0 when there are no items. */
  rangeEnd: number;
  /** Total item count across all pages. */
  totalCount: number;
}

/**
 * Slices a list into a page, clamping the requested page into range.
 *
 * Clamping matters because the list is live: a background refetch or a pushed
 * notification can shrink the list while the user sits on the last page, which would
 * otherwise leave them on an empty page.
 */
export const paginateNotifications = <T>(
  items: T[],
  requestedPage: number,
  pageSize: number = NOTIFICATIONS_PER_PAGE
): NotificationPage<T> => {
  const totalCount = items.length;
  const safePageSize = Math.max(1, pageSize);
  const numberOfPages = Math.max(1, Math.ceil(totalCount / safePageSize));
  const currentPage = Math.min(Math.max(1, requestedPage), numberOfPages);

  const startIndex = (currentPage - 1) * safePageSize;
  const pageItems = items.slice(startIndex, startIndex + safePageSize);

  return {
    pageItems,
    numberOfPages,
    currentPage,
    rangeStart: totalCount === 0 ? 0 : startIndex + 1,
    rangeEnd: startIndex + pageItems.length,
    totalCount
  };
};

/** Summary line shown beside the pagination control, e.g. "Showing 1-5 of 12 notifications". */
export const formatPageSummary = (page: Pick<NotificationPage<unknown>, 'rangeStart' | 'rangeEnd' | 'totalCount'>): string => {
  if (page.totalCount === 0) {
    return 'No notifications';
  }
  const noun = page.totalCount === 1 ? 'notification' : 'notifications';
  if (page.rangeStart === page.rangeEnd) {
    return `Showing ${page.rangeStart} of ${page.totalCount} ${noun}`;
  }
  return `Showing ${page.rangeStart}-${page.rangeEnd} of ${page.totalCount} ${noun}`;
};
